import { useContext, useEffect } from 'react'
import { GuestContext } from '../Guest.context'
import { useAxios } from './useAuthorizedAxios'
import { AxiosError, AxiosResponse } from 'axios'
import { PreflightData, PreflightResponseBody } from '../types'
import { changeLanguage } from 'i18next'
import { DateTime, Duration } from 'luxon'

const LANGUAGE_FALLBACKS = {
    'de-DE': 'de',
    'en-GB': 'en',
    'fr-FR': 'fr',
    'it-IT': 'it',
    // TODO: check Danish lots
    'da-DK': 'da',
    // TODO: check Polish lots
    'pl-PL': 'pl'
}

const URL_TOKEN_PARAM = 'access_token'

/**
 * This hook is used to set up the guest context.
 * It fetches the preflight data and sets the token in the axios instance.
 *
 * The GuestUI flow availible only with a valid access token.
 * The token is passed in the URL as a query parameter.
 *
 */
export const useSetupGuestContext = () => {
    const urlToken = new URLSearchParams(window.location.search).get(URL_TOKEN_PARAM)
    const { setToken, token, setPreflightData, setError, setLoading } = useContext(GuestContext)
    const axios = useAxios()

    useEffect(() => {
        if (urlToken && urlToken !== token) {
            setToken(urlToken)

            axios.defaults.headers.common.Authorization = urlToken

            axios
                .post(`${process.env.VITE_API_URL}customer/guest_preflight_request`)
                .then((response: AxiosResponse<{ body: string }>) => {
                    if (response.status !== 200) {
                        // TODO: handle error
                        // 400: expired_token
                        // 403: outside-opening-hours
                        // 404: insufficient-permissions
                        setError('Invalid response status')
                    }
                    const data: PreflightData = JSON.parse(response.data.body) as PreflightResponseBody

                    if (data.whitelist_duration) {
                        data.endDate = DateTime.local().plus(Duration.fromISO(data.whitelist_duration)).toISO()
                    }

                    setPreflightData(data)

                    changeLanguage(LANGUAGE_FALLBACKS[data.language as keyof typeof LANGUAGE_FALLBACKS] || 'de')
                    setLoading(false)
                })
                .catch((error: AxiosError) => {
                    console.error(error)
                    setError(error.response?.statusText || 'Unknown error')
                    setLoading(false)
                })
        }

        if (!urlToken && !token) {
            setLoading(false)
            setError('No token provided')
        }
    }, [urlToken, token])
}
